// ============================================================================
// NPSDContactInfo.tsx - Shared phone and email management component for NPSD screens
// Converted from Vue 2 Vue-NPSD-ContactInfo.ts
// ============================================================================
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';

// External globals - lazy lookups to avoid capturing before registration
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');

export interface CodeOption {
  code: string | number;
  value: string;
}

export interface PhoneEntry {
  uuid?: string;
  id?: string | number;
  phoneType: number;
  phoneNumber: string;
  isPrimary: boolean;
}

export interface EmailEntry {
  uuid?: string;
  id?: string | number;
  emailType: number;
  email: string;
  isPrimary: boolean;
}

export interface NPSDContactInfoProps {
  phones?: PhoneEntry[];
  emails?: EmailEntry[];
  isReadOnly?: boolean;
  phoneTypes?: CodeOption[];
  emailTypes?: CodeOption[];
  title?: string;
  onPhonesChange?: (phones: PhoneEntry[]) => void;
  onEmailsChange?: (emails: EmailEntry[]) => void;
}

export function NPSDContactInfo({
  phones = [],
  emails = [],
  isReadOnly = false,
  phoneTypes,
  emailTypes,
  title = 'Contact Information',
  onPhonesChange,
  onEmailsChange,
}: NPSDContactInfoProps) {
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  const isEmittingRef = useRef(false);

  // Resolve default types from globals if not provided via props
  const resolvedPhoneTypes = useMemo(() => {
    if (phoneTypes) return phoneTypes;
    const pot = (window as any).PhoneOptionTypes;
    if (typeof pot !== 'undefined') return pot;
    return [];
  }, [phoneTypes]);

  const resolvedEmailTypes = useMemo(() => {
    if (emailTypes) return emailTypes;
    const eot = (window as any).EmailOptionTypes;
    if (typeof eot !== 'undefined') return eot;
    return [];
  }, [emailTypes]);

  // Computed counts
  const phoneCount = useMemo(() => phones?.length || 0, [phones]);
  const emailCount = useMemo(() => emails?.length || 0, [emails]);

  // Mobile check (debounced to avoid excessive re-renders during resize)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // --- Phone operations ---

  const addPhone = useCallback(() => {
    if (isReadOnly || isEmittingRef.current) return;
    isEmittingRef.current = true;

    const newPhone: PhoneEntry = {
      uuid: getInsecureUUID(),
      phoneType: Number(resolvedPhoneTypes[0]?.code) || 1,
      phoneNumber: '',
      isPrimary: phoneCount === 0,
    };
    const updatedPhones = [...phones, newPhone];
    onPhonesChange?.(updatedPhones);

    requestAnimationFrame(() => {
      isEmittingRef.current = false;
    });
  }, [isReadOnly, phones, phoneCount, resolvedPhoneTypes, onPhonesChange]);

  const removePhone = useCallback(
    (index: number) => {
      if (isReadOnly || isEmittingRef.current) return;
      isEmittingRef.current = true;

      const removedPhone = phones[index];
      const updatedPhones = phones.filter((_, i) => i !== index);

      // If removed phone was primary and there are remaining phones, make first one primary
      if (removedPhone?.isPrimary && updatedPhones.length > 0) {
        updatedPhones[0] = { ...updatedPhones[0], isPrimary: true };
      }

      onPhonesChange?.(updatedPhones);

      requestAnimationFrame(() => {
        isEmittingRef.current = false;
      });
    },
    [isReadOnly, phones, onPhonesChange]
  );

  const updatePhone = useCallback(
    (index: number, field: string, value: any) => {
      if (isReadOnly || isEmittingRef.current) return;
      isEmittingRef.current = true;

      const updatedPhones = phones.map((phone, i) => {
        if (i === index) {
          return { ...phone, [field]: value };
        }
        return phone;
      });
      onPhonesChange?.(updatedPhones);

      requestAnimationFrame(() => {
        isEmittingRef.current = false;
      });
    },
    [isReadOnly, phones, onPhonesChange]
  );

  const togglePhonePrimary = useCallback(
    (index: number) => {
      if (isReadOnly || isEmittingRef.current) return;
      isEmittingRef.current = true;

      const updatedPhones = phones.map((phone, i) => ({
        ...phone,
        isPrimary: i === index,
      }));
      onPhonesChange?.(updatedPhones);

      requestAnimationFrame(() => {
        isEmittingRef.current = false;
      });
    },
    [isReadOnly, phones, onPhonesChange]
  );

  // --- Email operations ---

  const addEmail = useCallback(() => {
    if (isReadOnly || isEmittingRef.current) return;
    isEmittingRef.current = true;

    const newEmail: EmailEntry = {
      uuid: getInsecureUUID(),
      emailType: Number(resolvedEmailTypes[0]?.code) || 1,
      email: '',
      isPrimary: emailCount === 0,
    };
    const updatedEmails = [...emails, newEmail];
    onEmailsChange?.(updatedEmails);

    requestAnimationFrame(() => {
      isEmittingRef.current = false;
    });
  }, [isReadOnly, emails, emailCount, resolvedEmailTypes, onEmailsChange]);

  const removeEmail = useCallback(
    (index: number) => {
      if (isReadOnly || isEmittingRef.current) return;
      isEmittingRef.current = true;

      const removedEmail = emails[index];
      const updatedEmails = emails.filter((_, i) => i !== index);

      // If removed email was primary and there are remaining emails, make first one primary
      if (removedEmail?.isPrimary && updatedEmails.length > 0) {
        updatedEmails[0] = { ...updatedEmails[0], isPrimary: true };
      }

      onEmailsChange?.(updatedEmails);

      requestAnimationFrame(() => {
        isEmittingRef.current = false;
      });
    },
    [isReadOnly, emails, onEmailsChange]
  );

  const updateEmail = useCallback(
    (index: number, field: string, value: any) => {
      if (isReadOnly || isEmittingRef.current) return;
      isEmittingRef.current = true;

      const updatedEmails = emails.map((email, i) => {
        if (i === index) {
          return { ...email, [field]: value };
        }
        return email;
      });
      onEmailsChange?.(updatedEmails);

      requestAnimationFrame(() => {
        isEmittingRef.current = false;
      });
    },
    [isReadOnly, emails, onEmailsChange]
  );

  const toggleEmailPrimary = useCallback(
    (index: number) => {
      if (isReadOnly || isEmittingRef.current) return;
      isEmittingRef.current = true;

      const updatedEmails = emails.map((email, i) => ({
        ...email,
        isPrimary: i === index,
      }));
      onEmailsChange?.(updatedEmails);

      requestAnimationFrame(() => {
        isEmittingRef.current = false;
      });
    },
    [isReadOnly, emails, onEmailsChange]
  );

  // --- Label helpers ---

  const getPhoneTypeLabel = useCallback(
    (code: number): string => {
      const phoneType = resolvedPhoneTypes.find((t) => t.code === code);
      return phoneType?.value || '';
    },
    [resolvedPhoneTypes]
  );

  const getEmailTypeLabel = useCallback(
    (code: number): string => {
      const emailType = resolvedEmailTypes.find((t) => t.code === code);
      return emailType?.value || '';
    },
    [resolvedEmailTypes]
  );

  // --- Key helper ---

  const getItemKey = useCallback(
    (item: PhoneEntry | EmailEntry, prefix: string, index: number): string => {
      return item.uuid || String(item.id || '') || `${prefix}-${index}`;
    },
    []
  );

  return (
    <div className={`panel contact-panel${theme ? ' ' + theme : ''}`}>
      <div className="panel-header">
        {title}
        {!isReadOnly && (
          <div className="header-buttons">
            <button
              type="button"
              className="kstc-btn kstc-btn--success kstc-btn--sm"
              onClick={addPhone}
            >
              <i className="fas fa-plus"></i> Phone
            </button>
            <button
              type="button"
              className="kstc-btn kstc-btn--success kstc-btn--sm"
              onClick={addEmail}
            >
              <i className="fas fa-plus"></i> Email
            </button>
          </div>
        )}
      </div>
      <div className="panel-body">
        {/* Phone Numbers */}
        <div className="contact-section">
          <div className="contact-label">Phone:</div>
          <div className="contact-items">
            {phoneCount === 0 && (
              <div className="empty-state">No phone numbers</div>
            )}
            {phones.map((phone, index) => (
              <div
                key={getItemKey(phone, 'phone', index)}
                className="contact-item"
              >
                <div className="contact-item-header">
                  <Dropdownlist
                    value={phone.phoneType}
                    items={resolvedPhoneTypes}
                    labelKey="value"
                    valueKey="code"
                    searchable={false}
                    disabled={isReadOnly}
                    onItemChange={(item) =>
                      updatePhone(index, 'phoneType', item ? item.code : null)
                    }
                  />
                  {!isReadOnly && (
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removePhone(index)}
                    >
                      X
                    </button>
                  )}
                </div>
                <input
                  type="tel"
                  value={phone.phoneNumber}
                  onChange={(e) =>
                    updatePhone(index, 'phoneNumber', e.target.value)
                  }
                  disabled={isReadOnly}
                  className="kstc-input"
                  placeholder="Enter phone #"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Email Addresses */}
        <div className="contact-section">
          <div className="contact-label">Email:</div>
          <div className="contact-items">
            {emailCount === 0 && (
              <div className="empty-state">No email addresses</div>
            )}
            {emails.map((email, index) => (
              <div
                key={getItemKey(email, 'email', index)}
                className="contact-item"
              >
                <div className="contact-item-header">
                  <Dropdownlist
                    value={email.emailType}
                    items={resolvedEmailTypes}
                    labelKey="value"
                    valueKey="code"
                    searchable={false}
                    disabled={isReadOnly}
                    onItemChange={(item) =>
                      updateEmail(index, 'emailType', item ? item.code : null)
                    }
                  />
                  {!isReadOnly && (
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeEmail(index)}
                    >
                      X
                    </button>
                  )}
                </div>
                <input
                  type="email"
                  value={email.email}
                  onChange={(e) =>
                    updateEmail(index, 'email', e.target.value)
                  }
                  disabled={isReadOnly}
                  className="kstc-input"
                  placeholder="Enter email"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDContactInfoReact = NPSDContactInfo;
